// THE UNBOOKED TRADES ON CONVERT'S HOME (his "yes, build one list of unbooked trades, show it into convert home screen",
// 4 Nov 2026). The venue's fills used to show only once an exchange and both assets were picked, so a day he never opened
// was a day he never saw. One list across every desk exchange account: the fills no booked conversion used, last 30 days
// (Mechanisms' GET /api/unbooked-trades), one line per account · Dubai day · pair · side, newest first — what came in,
// what went out, the rate as the venue gave it (never rounded, rule 10) and the fee. A line opens Convert on that
// account with the To asset and the day: the form presets that day's fills. Both consoles draw it in their own frame;
// a booked line drops off at the next read.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type UnbookedTradeLine } from '../lib/api';
import { displayAsset, fmtDay, fmtRateAsGiven } from '../lib/format';
import { railName } from '../lib/naming';
import type { Desk } from './DeskSwitch';
import { ReceiptAmount } from './v2/Receipt';
import { EmptyState, ReadFailed } from './kit';
import { Pending } from './Pending';

export const UNBOOKED_DAYS = 30;

/** Account · day · pair · In · Out · Rate · Fee · the door, from lg; each column as wide as its widest cell. */
const COLS = 'lg:grid lg:grid-cols-[max-content_max-content_max-content_minmax(max-content,1fr)_minmax(max-content,1fr)_max-content_max-content_max-content] lg:gap-x-4';
const ROW = 'lg:col-span-full lg:grid lg:grid-cols-subgrid lg:items-baseline';

/** Where a line opens: Convert on its account, the From asset its own, the To asset and the day the form presets. */
export function unbookedHref(l: UnbookedTradeLine, entity: Desk): string {
  const p = new URLSearchParams();
  if (entity !== 'dubaiotc') p.set('entity', entity);
  p.set('rail', l.account.rail); p.set('asset', l.from.asset); p.set('to', l.to.asset); p.set('day', l.day);
  return `/swap?${p.toString()}`;
}

function Fee({ fees }: { fees: Record<string, string> }) {
  const paid = Object.entries(fees).filter(([, v]) => Number(v) !== 0);
  if (paid.length === 0) return <span className="text-tg-hint">—</span>;
  return <>{paid.map(([a, v], i) => <span key={a}>{i > 0 && ' · '}<ReceiptAmount value={v} asset={a} /></span>)}</>;
}

function Line({ l, entity, pad }: { l: UnbookedTradeLine; entity: Desk; pad: string }) {
  const [base = '', quote = ''] = l.symbol.split('/');
  const pair = `${displayAsset(base)}/${displayAsset(quote)} · ${l.side === 'sell' ? 'Sold' : 'Bought'}`;
  const rate = l.rate == null
    ? <span className="text-tg-hint">—</span>
    : <span className="whitespace-nowrap font-mono tabular">{fmtRateAsGiven(l.rate)}</span>;
  return (
    <Link to={unbookedHref(l, entity)} data-unbooked={`${l.account.rail}|${l.day}|${l.symbol}|${l.side}`}
      className={`${ROW} block border-b border-stroke-soft py-3 last:border-b-0 hover:bg-panel-2 ${pad}`}>
      <span className="flex items-baseline justify-between gap-3 lg:contents">
        <span className="min-w-0 text-body font-medium lg:whitespace-nowrap">{railName({ slug: l.account.rail })}</span>
        <span className="whitespace-nowrap font-mono tabular text-meta max-lg:ml-auto lg:text-body">{fmtDay(l.day)}</span>
      </span>
      <span className="block whitespace-nowrap text-meta text-tg-hint lg:text-body lg:text-tg-text">{pair}</span>
      <span className="text-right max-lg:hidden"><ReceiptAmount value={l.to.amount} asset={l.to.asset} /></span>
      <span className="text-right max-lg:hidden"><ReceiptAmount value={l.from.amount} asset={l.from.asset} /></span>
      <span className="text-right max-lg:hidden">{rate}</span>
      <span className="whitespace-nowrap text-right max-lg:hidden"><Fee fees={l.fees} /></span>
      <span className="btn-link whitespace-nowrap text-body max-lg:hidden">Convert →</span>
      {/* The phone: each figure on its own labelled row (a 390 px row cannot hold two amounts and a rate). */}
      <span className="mt-1.5 block space-y-0.5 lg:hidden">
        {([['In', <ReceiptAmount key="i" value={l.to.amount} asset={l.to.asset} />], ['Out', <ReceiptAmount key="o" value={l.from.amount} asset={l.from.asset} />],
          ['Rate', rate], ['Fee', <Fee key="f" fees={l.fees} />]] as const).map(([k, v]) => (
          <span key={k} className="flex items-baseline justify-between gap-3 text-body"><span className="text-meta text-tg-hint">{k}</span>{v}</span>
        ))}
        <span className="btn-link mt-1 block text-body">Convert →</span>
      </span>
    </Link>
  );
}

/** The list, drawn inside the console's own frame: `frame(title, count, body)`. `flush`: the frame has no padding of its
 *  own (the rebuilt console's Panel), so the rows carry it. */
export function UnbookedTrades({ entity, frame, flush = true }: {
  entity: Desk; frame: (title: string, count: number | null, body: ReactNode) => ReactNode; flush?: boolean;
}) {
  const pad = flush ? 'px-4 lg:px-5' : '';
  const q = useQuery({ queryKey: ['unbooked-trades', UNBOOKED_DAYS], queryFn: () => api.unbookedTrades(UNBOOKED_DAYS) });
  const lines = (q.data?.lines ?? []).filter((l) => l.account.entity === entity);
  const body = q.isError
    ? <div className={`py-3 ${pad}`}><ReadFailed what="the unbooked trades" error={q.error} onRetry={() => void q.refetch()} /></div>
    : q.isPending
      ? <div className={`py-3 ${pad}`}><Pending inline /></div>
      : lines.length === 0
        ? <div className={`py-3 ${pad}`}><EmptyState className="!py-0">Nothing unbooked.</EmptyState></div>
        : (
          <div className={`${COLS} text-body`}>
            <div className={`${ROW} border-b border-stroke-soft pb-1 pt-3 ${flush ? 'lg:px-5' : ''} text-label font-medium uppercase tracking-wider text-tg-hint max-lg:hidden`}>
              <span>Account</span><span>Day</span><span>Trade</span><span className="text-right">In</span><span className="text-right">Out</span>
              <span className="text-right">Rate</span><span className="text-right">Fee</span><span />
            </div>
            {lines.map((l) => <Line key={`${l.account.rail}|${l.day}|${l.symbol}|${l.side}`} l={l} entity={entity} pad={pad} />)}
          </div>
        );
  return <>{frame(`Unbooked trades · last ${UNBOOKED_DAYS} days`, q.isSuccess ? lines.length : null, body)}</>;
}
